import {
  DIGITANE_VIDEO_EMBED_PATH,
  DIGITANE_VIDEO_EMBED_TIMEOUT_MS,
  getDigitaneVideoUrl,
} from './config'
import type { DigitaneVideoApi } from './types'

const EMBED_SCRIPT_ID = 'digitane-video-embed'

/** 読み込み中の Promise。同時に呼ばれても script を二重に挿入しないよう共有する */
let pending: Promise<DigitaneVideoApi> | null = null

/** 失敗した読み込みの回数。失敗の後の読み込みでは URL に付けて、毎回新しく取得する */
let failedAttempts = 0

/**
 * embed.js を読み込み、window.DigitaneVideo を返す。
 *
 * - 読み込み済み（window.DigitaneVideo がある）なら即座に解決する
 * - 読み込み中なら同じ Promise を返す
 * - 失敗した場合は次回の呼び出しで再試行できる
 * - 一定時間（DIGITANE_VIDEO_EMBED_TIMEOUT_MS）で終わらなければ失敗として扱い
 *   （script を外して reject する）、次の呼び出しで再試行できる。応答もエラーも
 *   返らないまま止まっても、止まった Promise を返し続けない
 * - タイムアウトや error で失敗にする前に window.DigitaneVideo を確かめ、あれば
 *   失敗にせず解決する（別の script の評価で API が定義されていることがある）
 * - 失敗の後の読み込みでは、URL に retry=<失敗の回数> を付ける。応答が返らない
 *   まま止まった取得は script を外しても続き、同じ URL の script を入れ直すと、
 *   ブラウザ（Chrome）は新しい要求を送らずその取得を使い回すため。最初の読み込みの
 *   URL は変えないので、通常時のキャッシュの効き方は変わらない。embed.js は自身の
 *   src のオリジンだけを使うため、クエリを付けても動作は変わらない
 *
 * 失敗のログ出力は呼び出し側の責務（ここでは console.error を出さない）。
 */
export const loadDigitaneVideo = (): Promise<DigitaneVideoApi> => {
  const loaded = window.DigitaneVideo
  if (loaded) return Promise.resolve(loaded)
  if (pending) return pending

  const baseUrl = getDigitaneVideoUrl()
  if (baseUrl === null) {
    return Promise.reject(
      new Error('[ERROR] VITE_DIGITANE_VIDEO_URL is not configured'),
    )
  }

  const src =
    failedAttempts > 0 ?
      `${baseUrl}${DIGITANE_VIDEO_EMBED_PATH}?retry=${failedAttempts}`
    : `${baseUrl}${DIGITANE_VIDEO_EMBED_PATH}`
  // embed.js は評価時に document.currentScript.src から配信元オリジンを確定する。
  // type="module" の script では currentScript が null になるため、type は付けない
  const script = document.createElement('script')
  script.id = EMBED_SCRIPT_ID
  script.src = src
  script.async = true

  const promise = new Promise<DigitaneVideoApi>((resolve, reject) => {
    // pending を戻すのは、pending が自分の Promise のときだけにする。
    // タイムアウトで見限った script は、DOM から外しても通信は止まらず、あとから
    // load / error が届くことがある。そのときに、次の呼び出しで作った新しい
    // pending を消さないため。resolve / reject は 1 回目だけが効くので、
    // 遅れて届いた分は pending さえ守れば無害
    const releasePending = () => {
      if (pending === promise) pending = null
    }

    // load / error / タイムアウトのどの経路も、ここで決着させる。reject する前に、
    // 必ず window.DigitaneVideo を確かめ、あれば失敗にせず resolve する。
    // 別の script の評価で API が定義されていることがあるため。たとえばタイムアウト
    // で見限った古い script は、DOM から外しても通信は止まらず、再試行の script を
    // 待っている間に遅れて評価されうる。そのあとで再試行の script がタイムアウトや
    // error になっても、API が使えるのに読み込みエラーにしないようにする。
    // resolve のときも pending は戻す。以後の呼び出しは先頭の window.DigitaneVideo
    // の確認で即座に返るので、pending を残す必要はない。API があって resolve する
    // ときは、失敗の回数に数えない
    const settle = (failure: Error) => {
      clearTimeout(timer)
      releasePending()
      const api = window.DigitaneVideo
      if (api) {
        resolve(api)
        return
      }
      failedAttempts += 1
      reject(failure)
    }

    // load / error のどちらも届かないまま止まると、pending が未解決のまま残り、
    // 通信が回復しても次の呼び出しが止まった Promise を受け取り続ける。
    // 一定時間で見限って失敗にし、次の呼び出しで読み込み直せるようにする。
    // 待っていた script は、API があって resolve するときも DOM から外す
    // （load すればまた評価されるだけで害はないが、状態をそろえる）
    const timer = setTimeout(() => {
      script.remove()
      settle(new Error(`[ERROR] Timed out loading ${src}`))
    }, DIGITANE_VIDEO_EMBED_TIMEOUT_MS)

    script.addEventListener('load', () => {
      // 読み込めても API が定義されなかったときだけ失敗になる
      settle(
        new Error(
          '[ERROR] DigitaneVideo is not defined after loading embed.js',
        ),
      )
    })
    script.addEventListener('error', () => {
      script.remove()
      settle(new Error(`[ERROR] Failed to load ${src}`))
    })
  })

  // load / error は挿入後に届くが、念のため先に pending を確定させてから挿入する
  // （タイマーは上で張ってあるので、同期的に届いても止められる）
  pending = promise
  document.head.appendChild(script)
  return promise
}
